import { ButtonType, ButtonWidget, InlineAlertType, InlineAlertWidget, Tooltip } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IPackage } from '~src/packages/models/IPackage';

import { IPackageComplianceResult, PackageComplianceEvaluator } from '../utils/PackageComplianceEvaluator';

export const ALLOWED_ORIGINS_IMPACT_WARNING_TEST_ID = 'allowed-origins-impact-warning';
export const ALLOWED_ORIGINS_NEWLY_NON_COMPLIANT_TEST_ID = 'allowed-origins-newly-non-compliant-alert';
export const ALLOWED_ORIGINS_NEWLY_COMPLIANT_TEST_ID = 'allowed-origins-newly-compliant-alert';
export const ALLOWED_ORIGINS_NON_CANONICAL_TEST_ID = 'allowed-origins-non-canonical-alert';
export const ALLOWED_ORIGINS_BULK_REMEDIATION_BUTTON_TEST_ID = 'allowed-origins-bulk-remediation-button';

interface PackageImpactWarningProps {
  readonly packages: readonly IPackage[];
  /** The setting's origins as currently persisted (i.e. as of page load). */
  readonly currentOrigins: ReadonlySet<string>;
  /** The setting's origins as the admin is currently editing them, not yet saved. */
  readonly pendingOrigins: ReadonlySet<string>;
}

interface IImpact {
  readonly newlyNonCompliant: IPackageComplianceResult[];
  readonly newlyCompliant: IPackageComplianceResult[];
  readonly nonCanonical: IPackageComplianceResult[];
}

/**
 * Live, symmetric compliance-impact preview shown while editing the allowed-origins list:
 * which in-use packages this edit would newly disable (removing an origin they rely on) and
 * which it would newly re-enable (adding an origin they were missing) -- computed entirely
 * client-side via {@link PackageComplianceEvaluator}, with no backend round-trip.
 *
 * This preview is optimistic and non-authoritative: the server never persists a compliance
 * verdict -- it enforces live at serve time instead (see PackageContentController.java) and
 * disables non-compliant packages via a backgrounder when the allow-list changes. There is no
 * server value to reconcile against here by design; see {@link PackageComplianceEvaluator}.
 */
export function PackageImpactWarning(props: PackageImpactWarningProps): React.ReactElement | null {
  const { packages, currentOrigins, pendingOrigins } = props;
  const impact = computeImpact(packages, currentOrigins, pendingOrigins);

  if (
    impact.newlyNonCompliant.length === 0 &&
    impact.newlyCompliant.length === 0 &&
    impact.nonCanonical.length === 0
  ) {
    return null;
  }

  return (
    <div data-tb-test-id={ALLOWED_ORIGINS_IMPACT_WARNING_TEST_ID}>
      {impact.newlyNonCompliant.length > 0 && (
        <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
          <InlineAlertWidget
            type={InlineAlertType.Warning}
            testId={ALLOWED_ORIGINS_NEWLY_NON_COMPLIANT_TEST_ID}
            children={Messages.allowedOriginsNewlyNonCompliantMessage({
              COUNT: impact.newlyNonCompliant.length.toString(),
              PACKAGE_NAMES: packageNames(impact.newlyNonCompliant),
            })}
          />
          <BulkRemediationPlaceholder />
        </div>
      )}
      {impact.newlyCompliant.length > 0 && (
        <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
          <InlineAlertWidget
            type={InlineAlertType.Info}
            testId={ALLOWED_ORIGINS_NEWLY_COMPLIANT_TEST_ID}
            children={Messages.allowedOriginsNewlyCompliantMessage({
              COUNT: impact.newlyCompliant.length.toString(),
              PACKAGE_NAMES: packageNames(impact.newlyCompliant),
            })}
          />
        </div>
      )}
      {impact.nonCanonical.length > 0 && (
        <div style={{ marginBottom: CommonStyles.Sizes.MediumSpace }}>
          <InlineAlertWidget
            type={InlineAlertType.Info}
            testId={ALLOWED_ORIGINS_NON_CANONICAL_TEST_ID}
            children={Messages.allowedOriginsNonCanonicalSourceMessage({
              COUNT: impact.nonCanonical.length.toString(),
              PACKAGE_NAMES: packageNames(impact.nonCanonical),
            })}
          />
        </div>
      )}
    </div>
  );
}

function computeImpact(
  packages: readonly IPackage[],
  currentOrigins: ReadonlySet<string>,
  pendingOrigins: ReadonlySet<string>
): IImpact {
  const current = PackageComplianceEvaluator.evaluateAll(packages, currentOrigins);
  const pending = PackageComplianceEvaluator.evaluateAll(packages, pendingOrigins);

  const newlyNonCompliant: IPackageComplianceResult[] = [];
  const newlyCompliant: IPackageComplianceResult[] = [];
  const nonCanonical: IPackageComplianceResult[] = [];

  pending.forEach((pendingResult, index) => {
    const currentResult = current[index];
    if (pendingResult.hasNonCanonicalSource) {
      nonCanonical.push(pendingResult);
    } else if (currentResult.isCompliant && !pendingResult.isCompliant) {
      newlyNonCompliant.push(pendingResult);
    } else if (!currentResult.isCompliant && pendingResult.isCompliant) {
      newlyCompliant.push(pendingResult);
    }
  });

  return { newlyNonCompliant, newlyCompliant, nonCanonical };
}

function packageNames(results: readonly IPackageComplianceResult[]): string {
  return results.map(r => r.pkg.name ?? r.pkg.manifestId ?? r.pkg.luid ?? '').join(', ');
}

// TODO(W-23836064 phase 2, see analysis/w-23836064-allowedorigins-site-setting-phase1.md and
// analysis/w-23836064-phase5-persisted-compliance-status.md): there is no backend bulk
// remediation action (e.g. auto-disabling newly-non-compliant packages) to call yet. This
// button is a visibly-disabled placeholder for that future action rather than omitted
// entirely, per the agreed scope for this editor.
function BulkRemediationPlaceholder(): React.ReactElement {
  return (
    <Tooltip tooltipContent={<span>{Messages.allowedOriginsBulkRemediationTooltip()}</span>}>
      <span>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId={ALLOWED_ORIGINS_BULK_REMEDIATION_BUTTON_TEST_ID}
          disabled={true}
          style={{ marginTop: CommonStyles.Sizes.SmallSpace }}
        >
          {Messages.allowedOriginsBulkRemediationButton()}
        </ButtonWidget>
      </span>
    </Tooltip>
  );
}
